'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const pains = [
  {
    title: 'The team you bought is not the team you have',
    body: 'Acquisitions reassign pods and account managers mid-engagement. Your institutional knowledge walked out in a reorg you were not part of.',
  },
  {
    title: 'Pooled reps mean nobody owns your number',
    body: "When a rep splits time across six clients, your pipeline is whoever's priority it is that week.",
  },
  {
    title: 'Your locked contract protects them, not you',
    body: 'Multi-quarter commitments price in their churn risk. Month-to-month terms mean the vendor has to earn every renewal.',
  },
];

export default function SwitchHero() {
  const rightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    const els = rightRef?.current?.querySelectorAll('.rev');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <header
      className="py-24"
      style={{
        background:
          'radial-gradient(900px 500px at 85% -10%, rgba(201,137,65,0.15), transparent 60%), var(--navy-deep)',
      }}
    >
      <div className="max-w-site mx-auto px-7">
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-16 items-start">
          <div>
            <div className="inline-flex font-mono text-[12px] tracking-[0.18em] uppercase text-primary border border-primary/50 px-4 py-2 rounded-full mb-6">
              For teams whose provider changed under them
            </div>
            <h1 className="heading-land text-white">
              Your agency got acquired. Your results got reassigned.
            </h1>
            <p className="mt-5 text-[19px] text-muted-cream leading-relaxed max-w-[56ch]">
              The outsourced SDR market consolidated hard through 2025 and 2026. If your pod was
              reshuffled, your account manager vanished, or the reporting went quiet mid-contract,
              you are not imagining the drop. Switching is a 30-day decision here.
            </p>
            <div className="flex flex-wrap gap-3.5 mt-9">
              <a
                href="/request-meeting"
                className="inline-flex items-center gap-2.5 font-semibold text-sm px-6 py-3.5 rounded-[10px] bg-primary text-navy-deep border border-transparent transition-all duration-200 hover:bg-gold-bright hover:-translate-y-px hover:shadow-gold"
              >
                Book a transition call
              </a>
              <Link
                href="/homepage"
                className="inline-flex items-center gap-2.5 font-semibold text-sm px-6 py-3.5 rounded-[10px] border border-line-navy text-white transition-all duration-200 hover:border-primary hover:text-primary"
              >
                See how we work
              </Link>
            </div>
          </div>

          <div ref={rightRef} className="pt-4">
            {pains?.map((p, i) => (
              <div
                key={p?.title}
                className="rev pain-item"
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <b className="block text-[16.5px] mb-1 text-white font-semibold">{p?.title}</b>
                <p className="text-[15px] text-muted-cream leading-relaxed">{p?.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}